import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, ArchiveRestore, FileText, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecycleBin() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmEntry, setConfirmEntry] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/trash`, { withCredentials: true });
      setEntries(data || []);
    } catch (e) {
      toast.error('Failed to load recycle bin');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (entry) => {
    try {
      setBusyId(entry.id);
      const { data } = await axios.post(`${API}/trash/${entry.id}/restore`, {}, { withCredentials: true });
      toast.success(`Restored ${entry.record_type} "${entry.title}" (${data.restored_related_records} related records)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setBusyId(null);
    }
  };

  const purge = async () => {
    const entry = confirmEntry;
    setConfirmEntry(null);
    try {
      setBusyId(entry.id);
      await axios.delete(`${API}/trash/${entry.id}`, { withCredentials: true });
      toast.success('Permanently deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const daysLeft = (exp) => Math.max(0, Math.ceil((new Date(exp) - Date.now()) / 86400000));
  const fmt = (d) => new Date(d).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div className="p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#474747] flex items-center gap-2" data-testid="recycle-bin-title">
          <Trash2 className="w-6 h-6" /> Recycle Bin
        </h1>
        <p className="text-sm text-gray-500">Deleted applications and items can be restored for 30 days, then they're gone for good.</p>
      </div>

      <div className="bg-white rounded-xl border border-[#E9E9E9] overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead className="bg-gray-50 border-b border-[#E9E9E9]">
            <tr>
              {['Type', 'Record', 'Stage', 'Related Data', 'Deleted', 'Restore Window', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E9E9E9]">
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-gray-500" data-testid="recycle-bin-empty">Recycle bin is empty — deleted applications and items will appear here</td></tr>
            ) : entries.map(e => (
              <tr key={e.id} className="hover:bg-gray-50" data-testid={`trash-row-${e.id}`}>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${e.record_type === 'application' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                    {e.record_type === 'application' ? <FileText className="w-3 h-3" /> : <Package className="w-3 h-3" />}
                    {e.record_type}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <p className="text-sm font-semibold text-[#474747]">{e.title}</p>
                  {e.reference_code && <p className="text-xs text-gray-400">{e.reference_code}</p>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">{e.lifecycle_stage || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {Object.entries(e.related_counts || {}).map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(', ') || '—'}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{fmt(e.deleted_at)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold ${daysLeft(e.expires_at) <= 5 ? 'text-red-600' : 'text-gray-600'}`}>
                    {daysLeft(e.expires_at)} days left
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <Button size="sm" disabled={busyId === e.id} onClick={() => restore(e)}
                      className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#1E2117] font-semibold" data-testid={`restore-button-${e.id}`}>
                      <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Restore
                    </Button>
                    <Button size="sm" variant="outline" disabled={busyId === e.id} onClick={() => setConfirmEntry(e)}
                      className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`purge-button-${e.id}`}>
                      Delete Forever
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirmEntry} onOpenChange={(o) => { if (!o) setConfirmEntry(null); }}>
        <AlertDialogContent data-testid="purge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete "{confirmEntry?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the {confirmEntry?.record_type} and all its related records from the recycle bin forever. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={purge} className="bg-red-600 hover:bg-red-700 text-white" data-testid="purge-confirm">
              Delete Forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
